import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Option } from '../../../../../shared/models/option.model';
import { InputConfig } from '../../../../../shared/ui/form/input/input.component.config';
import { SelectConfig } from '../../../../../shared/ui/form/select/select.component.config';
import { NotificationService } from '../../../../shared/services/notification.service';
import { SubmittedErrorStateMatcher } from '../../../../shared/utils/submitted-error-state.matcher';
import { legacyErrorMessage } from '../../../../shared/utils/legacy-api-response.util';
import { isValidBic, isValidIban, normalizeIban, trimmedRequiredValidator } from '../../../../shared/utils/bank-validators.util';
import { BankAccountCrudService } from '../../services/bank-account-crud.service';
import { BankAccountEntity, BankAccountFormDialogData, BankAccountPayload, BankBicOption } from '../../models/bank-account-form.model';
import { BankAccountFormLookupsFacade } from './bank-account-form-lookups.facade';

function optionCode(value: Option | string | null): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return String(value.id || value.display || '');
}

function swiftRequiredValidator(control: AbstractControl): ValidationErrors | null {
  return optionCode(control.value).trim() ? null : { required: true };
}

function swiftBicValidator(control: AbstractControl): ValidationErrors | null {
  const code = optionCode(control.value).trim();
  if (!code) return null;
  return isValidBic(code) ? null : { bic: true };
}

function ibanFormatValidator(control: AbstractControl): ValidationErrors | null {
  const value = String(control.value || '').trim();
  if (!value) return null;
  return isValidIban(value) ? null : { iban: true };
}

@Component({
  selector: 'app-bank-account-form-dialog',
  templateUrl: './bank-account-form-dialog.component.html',
  styleUrls: ['./bank-account-form-dialog.component.scss'],
  standalone: false,
  providers: [
    SubmittedErrorStateMatcher,
    { provide: ErrorStateMatcher, useExisting: SubmittedErrorStateMatcher },
  ],
})
export class BankAccountFormDialogComponent implements OnInit {
  readonly form = new FormGroup({
    swift: new FormControl<Option | string>('', { nonNullable: true, validators: [swiftRequiredValidator, swiftBicValidator] }),
    bank: new FormControl('', { nonNullable: true, validators: [trimmedRequiredValidator] }),
    iban: new FormControl('', { nonNullable: true, validators: [trimmedRequiredValidator, ibanFormatValidator] }),
  });

  entity: BankAccountEntity | null = null;
  bics: BankBicOption[] = [];
  bicsLoading = false;

  readonly swiftConfig: SelectConfig = {
    label: 'Código BIC/SWIFT — Nombre del banco',
    placeholder: 'Busca por código o nombre del banco',
    options: [],
    allowCustomValues: true,
    customValueLabel: 'Usar',
    suffixIcon: 'search',
    maxResults: 50,
    onFocus: () => this.ensureBics(),
    onChange: (value: Option) => this.onBicSelected(value),
    errors: { required: 'Indica el BIC/SWIFT', bic: 'El BIC/SWIFT no es válido' },
  };

  readonly bankConfig: InputConfig = {
    label: 'Entidad',
    maxLength: 255,
    errors: { required: 'Indica la entidad' },
  };

  readonly ibanConfig: InputConfig = {
    label: 'IBAN',
    placeholder: 'ES00 0000 0000 0000 0000 0000',
    maxLength: 42,
    uppercase: true,
    suffixIcon: 'credit_card',
    errors: { required: 'Indica el IBAN', iban: 'El IBAN no es válido' },
  };

  ready = false;
  loadError = '';
  saving = false;
  submitted = false;
  saveError = '';
  showUid = false;

  static open(dialog: MatDialog, data: BankAccountFormDialogData): Observable<boolean> {
    return dialog.open<BankAccountFormDialogComponent, BankAccountFormDialogData, boolean>(BankAccountFormDialogComponent, {
      width: '580px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private dialogRef: MatDialogRef<BankAccountFormDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: BankAccountFormDialogData,
    private crud: BankAccountCrudService,
    private lookups: BankAccountFormLookupsFacade,
    private notify: NotificationService,
    private errorState: SubmittedErrorStateMatcher,
  ) { }

  get isEdit(): boolean {
    return !!this.data && !!this.data.uid;
  }

  get title(): string {
    return this.isEdit ? 'Editar cuenta bancaria' : 'Nueva cuenta bancaria';
  }

  get subtitle(): string {
    if (this.isEdit && this.entity && this.entity.accountNumber) return 'Cuenta contable ' + this.entity.accountNumber;
    return this.isEdit ? 'Datos de la cuenta bancaria de la empresa' : 'Se creará su cuenta contable 572 automáticamente';
  }

  isInvalid(control: 'swift' | 'bank' | 'iban'): boolean {
    return this.submitted && this.form.controls[control].invalid;
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loadError = '';
    if (!this.isEdit) {
      this.applyEntity(null);
      this.ready = true;
      return;
    }

    const initial = this.data.bankAccount;
    if (initial && initial.uid === this.data.uid) {
      this.data.bankAccount = null;
      this.applyEntity(initial);
      this.ready = true;
      return;
    }

    this.ready = false;
    this.crud.get(this.data.uid as string)
      .then(entity => this.applyEntity(entity))
      .catch(error => { this.loadError = legacyErrorMessage(error, 'No se pudo cargar la cuenta bancaria.'); })
      .then(() => { this.ready = true; });
  }

  ensureBics(): void {
    if (this.bics.length || this.bicsLoading) return;
    this.bicsLoading = true;
    this.swiftConfig.hint = 'Cargando entidades…';
    this.lookups.bics()
      .then(list => {
        this.bics = list || [];
        this.swiftConfig.options = this.bics.map(bic => this.toOption(bic));
      })
      .catch(() => { this.bics = []; })
      .then(() => {
        this.bicsLoading = false;
        this.swiftConfig.hint = '';
      });
  }

  save(): void {
    if (this.saving) return;
    this.submitted = true;
    this.errorState.submitted = true;
    this.saveError = '';
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const payload = this.buildPayload();
    const uid = this.isEdit ? (this.entity && this.entity.uid) || (this.data.uid as string) : null;

    this.saving = true;
    this.dialogRef.disableClose = true;

    const request = uid ? this.crud.update(uid, payload) : this.crud.create(payload);
    request
      .then(() => {
        this.notify.ok(uid ? 'Cuenta bancaria actualizada correctamente' : 'Cuenta bancaria creada correctamente');
        this.dialogRef.disableClose = false;
        this.dialogRef.close(true);
      })
      .catch(error => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.saveError = legacyErrorMessage(error, uid ? 'No se pudo actualizar la cuenta bancaria' : 'No se pudo crear la cuenta bancaria');
      });
  }

  cancel(): void {
    if (this.saving) return;
    this.dialogRef.close(false);
  }

  private onBicSelected(value: Option): void {
    const code = optionCode(value).trim().toUpperCase();
    if (!code) return;
    const bic = this.bics.find(b => b.bic.toUpperCase() === code);
    if (bic && !this.form.controls.bank.value.trim()) {
      this.form.controls.bank.setValue(bic.bank);
    }
  }

  private toOption(bic: BankBicOption): Option {
    return {
      id: bic.bic,
      display: bic.bank ? bic.bic + ' · ' + bic.bank : bic.bic,
    };
  }

  private applyEntity(source: BankAccountEntity | null): void {
    this.entity = source ? {
      id: source.id,
      uid: source.uid,
      iban: source.iban || '',
      bank: source.bank || '',
      swift: source.swift || '',
      remesa: !!source.remesa,
      accountNumber: source.accountNumber || '',
      taxationDefault: !!source.taxationDefault,
      accountingModelsId: source.accountingModelsId,
      isAutoReconciliationEnabled: source.isAutoReconciliationEnabled,
    } : null;

    const swift = this.entity ? this.entity.swift : '';
    this.form.reset({
      swift: swift ? { id: swift, display: swift } : '',
      bank: this.entity ? this.entity.bank : '',
      iban: this.entity ? this.entity.iban : '',
    });
  }

  private buildPayload(): BankAccountPayload {
    const value = this.form.getRawValue();
    const base = this.entity;
    const payload: BankAccountPayload = {
      iban: normalizeIban(value.iban),
      bank: value.bank.trim(),
      swift: optionCode(value.swift).trim().toUpperCase(),
      remesa: base ? !!base.remesa : false,
      taxationDefault: base ? !!base.taxationDefault : false,
    };
    if (base) {
      if (base.id) payload.id = base.id;
      if (base.uid) payload.uid = base.uid;
      if (base.accountNumber) payload.accountNumber = base.accountNumber;
      if (base.accountingModelsId != null) payload.accountingModelsId = base.accountingModelsId;
      if (base.isAutoReconciliationEnabled != null) payload.isAutoReconciliationEnabled = base.isAutoReconciliationEnabled;
    }
    return payload;
  }
}
